/**
 * DrawerTableOfContents — table des matières repliable affichée en haut d'un
 * article dans le Help Drawer (équivalent du menu flottant du Centre d'aide
 * pleine page, adapté au format étroit du tiroir).
 *
 * Ne s'affiche qu'à partir de MIN_HEADINGS_FOR_TOC sections H2.
 *
 * Pièges gérés :
 *  - le scroll-spy écoute le viewport interne de la ScrollArea (pas `window`)
 *  - les ancres `#hash` natives ne fonctionnent pas dans un conteneur
 *    scrollable → on utilise `scrollIntoView` (qui respecte `scroll-mt-*`).
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, List } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { cn } from '../../lib/utils'
import { extractToc } from '../../lib/toc'
import { useScrollSpy } from '../../hooks/useScrollSpy'

const MIN_HEADINGS_FOR_TOC = 3

export function DrawerTableOfContents({
    content,
    getContainer,
}: {
    content: string
    getContainer: () => HTMLElement | null
}) {
    const { t } = useTranslation()
    const toc = useMemo(() => extractToc(content), [content])
    const activeSection = useScrollSpy(toc, { getContainer, offset: 96 })

    if (toc.length < MIN_HEADINGS_FOR_TOC) return null

    const handleNavigate = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <Collapsible className="mb-4 rounded-lg border border-border bg-muted/30">
            <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex items-center gap-2">
                    <List className="h-4 w-4 text-muted-foreground" />
                    {t('help.tableOfContents')}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
                <nav className="space-y-0.5 px-2 pb-2">
                    {toc.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => handleNavigate(item.id)}
                            className={cn(
                                'block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                activeSection === item.id
                                    ? 'bg-accent font-medium text-primary'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                        >
                            {item.text}
                        </button>
                    ))}
                </nav>
            </CollapsibleContent>
        </Collapsible>
    )
}
